import SingletonRouter, { type NextRouter } from 'next/router'
import { resolveHref } from 'next/dist/client/resolve-href'

import { isUntrackedPath } from './untracked-pages'

/**
 * The coach dashboard puts client ids in its URLs (/dashboard/clients/<id>,
 * ?clientId=<id>), a coach's video text links to /video/<token>, where the
 * token plays the client's video, and the Project Predictor text links to
 * /client-survey/<token>, where the token answers the client's question. GTM's Google tags and the ad pixels read
 * location.href on every page view and history change, so no tracker may run
 * in a document that has shown either kind of URL. (/video also loads no
 * PostHog and sends no Referer: utilities/untracked-pages.ts.)
 *
 * Two halves keep that true:
 * - Layout and _app never load GTM or the pixels on those routes.
 * - A document that already loaded them (any marketing or account page)
 *   enters them with a full page load, so the trackers in memory never
 *   see the dashboard URL through a pushState or popstate.
 */
const TRACKER_FREE_PATH = /^\/(dashboard|video|client-survey)(\/|$)/

/** Pathname of a relative or absolute URL (query and hash dropped). */
const pathOf = (url: string) => {
  if (typeof window === 'undefined') return url.split(/[?#]/)[0]
  return new URL(url, window.location.href).pathname
}

/**
 * Whether a URL or route pattern is under /dashboard, /video or
 * /client-survey, where no tracker may run.
 * @param url A path, URL, or Next route pattern such as `/dashboard/[page]`.
 */
export const isTrackerFreePath = (url = '') =>
  TRACKER_FREE_PATH.test(pathOf(url))

declare global {
  interface Window {
    google_tag_manager?: object
  }
}

/**
 * Whether this document has run GTM's loader or a consent-gated pixel.
 * GTM's snippet pushes `{ 'gtm.start' }` before gtm.js arrives, so this is
 * true from the moment the loader runs, not only once the container has booted.
 */
export const trackersLoaded = () => {
  if (typeof window === 'undefined') return false
  const gtmStarted = (window.dataLayer || []).some(
    entry => !!entry && typeof entry === 'object' && 'gtm.start' in entry
  )
  return (
    gtmStarted ||
    !!window.google_tag_manager ||
    !!window.fbq ||
    !!window.ttq ||
    !!window.uetq
  )
}

/**
 * A dashboard URL reached from a document that ran trackers needs a full load.
 * An untracked page (/video) always does: only a fresh document guarantees no
 * PostHog and its no-referrer policy.
 */
const mustHardNavigate = (url: string) =>
  isUntrackedPath(pathOf(url)) || (isTrackerFreePath(url) && trackersLoaded())

type Navigate = NextRouter['push']

let guardInstalled = false

/**
 * Makes every entry into /dashboard from a document that ran trackers a full
 * page load: router push/replace, Link, and Back/Forward. Call once the router
 * exists (an effect in _app); it patches the real router instance, which
 * useRouter(), Link and the singleton all delegate to. Idempotent.
 */
export const installTrackerFreeNavigationGuard = () => {
  const router = SingletonRouter.router as NextRouter | null
  if (typeof window === 'undefined' || !router || guardInstalled) return
  guardInstalled = true

  // The last address-bar URL the trackers saw (credential guard already run).
  let lastTrackedUrl = isTrackerFreePath(window.location.href)
    ? '/'
    : window.location.href
  SingletonRouter.events.on('routeChangeComplete', () => {
    if (!isTrackerFreePath(window.location.href)) {
      lastTrackedUrl = window.location.href
    }
  })

  // Back/forward into a dashboard entry: the address bar already shows the
  // dashboard URL. Two things must not run:
  // - Next's own popstate handler, which would soft-navigate and, in a
  //   production build, replaceState the dashboard URL before the page
  //   unloads. Browsers call window listeners in registration order and Next
  //   registers first, so it is vetoed through its beforePopState hook.
  // - GTM's popstate listener (and the pixels'). They register when their
  //   scripts load, after this listener, so it stops them.
  // Tags that beacon on unload (GA4's user_engagement) read the address bar
  // too, so the URL they already saw goes back first, and the dashboard loads
  // in its place.
  router.beforePopState(({ as }) => !mustHardNavigate(as))
  window.addEventListener('popstate', event => {
    if (!mustHardNavigate(window.location.href)) return
    event.stopImmediatePropagation()
    const target = window.location.href
    window.history.replaceState(null, '', lastTrackedUrl)
    window.location.replace(target)
  })

  // Links and router.push/replace: hand the URL to the browser instead of the
  // SPA router, before Next calls history.pushState (which GTM hooks).
  const guard =
    (soft: Navigate, method: 'assign' | 'replace'): Navigate =>
    (url, as, options) => {
      const [resolved, resolvedAs] = resolveHref(router, url, true)
      const target = as ? resolveHref(router, as) : resolvedAs || resolved
      if (mustHardNavigate(target)) {
        window.location[method](target)
        // Same as Next's own hard navigation: never settle, the page unloads.
        return new Promise<boolean>(() => {})
      }
      return soft(url, as, options)
    }
  router.push = guard(router.push.bind(router), 'assign')
  router.replace = guard(router.replace.bind(router), 'replace')
}
